import React from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { PageHero, Section, PrimaryButton, SecondaryButton } from "@/components/ui-helpers";
import { STANDARD_STEPS } from "@/data/content";

export default function AscendiaStandard() {
  return (
    <>
      <SEO title="The Ascendia Standard" description="What the Ascendia Standard means: systematic workflow, surface identification, professional equipment, detailing, microfiber rotation, top-to-bottom order and a final quality inspection." />
      <PageHero kicker="The Ascendia Standard" title="A method, not a maybe" subtitle="The Ascendia Standard is the systematic way we prepare every property — so the result is consistent, thorough and genuinely move-in ready." image="https://images.unsplash.com/photo-1600585152220-90363fe7e115?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600">
        <PrimaryButton to="/contact">Request a Quote <ArrowRight className="h-4 w-4" /></PrimaryButton>
      </PageHero>

      {/* Method statement — paper interlude */}
      <section className="paper-section section-y">
        <div className="container-ascendia">
          <div className="max-w-3xl">
            <p className="kicker mb-4">The method</p>
            <p className="display-3" style={{ color: "var(--paper-ink)" }}>
              Consistency is not luck. It's a repeatable sequence — surface by surface, top to bottom — executed the same meticulous way, every single visit.
            </p>
          </div>
        </div>
      </section>

      <Section>
        <ol className="grid md:grid-cols-2 gap-5">
          {STANDARD_STEPS.map((s, i) => (
            <Reveal key={s.t} delay={(i % 2) * 0.06}>
              <li className="ascendia-card p-7 h-full flex gap-5">
                <span className="editorial-index select-none leading-none flex-shrink-0" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-display text-lg font-semibold text-white u-tracking-tight">{s.t}</h3>
                  <p className="mt-2 text-sm text-secondary u-body">{s.d}</p>
                </div>
              </li>
            </Reveal>
          ))}
        </ol>
        <div className="mt-12 ascendia-card card-accent-top p-8 sm:p-10 text-center">
          <CheckCircle2 className="h-10 w-10 text-gold mx-auto" strokeWidth={1.5} />
          <h2 className="mt-4 display-2 text-white">Every job ends with a final inspection</h2>
          <p className="mt-4 lede mx-auto">We don't consider a job done until it has passed a structured walkthrough against the agreed scope.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <PrimaryButton to="/property-intelligence">Build your Property Plan <ArrowRight className="h-4 w-4" /></PrimaryButton>
            <SecondaryButton to="/what-we-use">What we use</SecondaryButton>
          </div>
        </div>
      </Section>
    </>
  );
}
